import { useCallback, useEffect, useRef, useState } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useSelector, useDispatch } from "react-redux";
import { selectTourRunning, selectTourType, endTour } from "../../features/tour/tourSlice";
import { useGetTenantPlanQuery } from "../../features/tenants/tenantsApiSlice";
import {
  hasFeatureAccess,
  normalizePlanId,
  FEATURE_KEYS,
  PLAN_IDS,
} from "../../utils/planFeatures";

const memberTourSteps = [
  {
    target: "[data-tour='member-greeting']",
    title: "Ihre Startseite",
    content:
      "Hier finden Sie alle wichtigen Bereiche auf einen Blick. Von hier aus erreichen Sie alles, was Sie brauchen.",
    featureKey: null,
  },
  {
    target: "[data-tour='member-meine-daten']",
    title: "Meine Daten",
    content:
      "Hier können Sie Ihre persönlichen Angaben und Ihren Vertrag einsehen.",
    featureKey: FEATURE_KEYS.MEMBER_PORTAL,
  },
  {
    target: "[data-tour='member-standorte']",
    title: "Standorte",
    content:
      "Hier finden Sie alle freigegebenen Standorte, Ihren eigenen Standort ganz oben und eine Detailansicht pro Standort.",
    featureKey: FEATURE_KEYS.MEMBER_PORTAL,
  },
  {
    target: "[data-tour='member-testing-fees']",
    title: "Prüfungsgebühren",
    content:
      "Behalten Sie die aktuell gültigen Prüfungsgebühren im Blick.",
    featureKey: FEATURE_KEYS.MEMBER_PORTAL,
  },
  {
    target: "[data-tour='member-news']",
    title: "Neuigkeiten",
    content:
      "Hier finden Sie aktuelle Nachrichten und Ankündigungen.",
    featureKey: FEATURE_KEYS.NEWS,
  },
  {
    target: "[data-tour='member-e-dojang']",
    title: "E-Dojang",
    content:
      "Dieser Bereich ist für Ihr Online-Lernen vorgesehen, inklusive Lehrvideos und Prüfungsprogramm.",
    featureKey: FEATURE_KEYS.E_DOJANG,
  },
  {
    target: "[data-tour='member-tasks']",
    title: "Aufgaben",
    content:
      "Ihre offenen Aufgaben und To-dos werden hier angezeigt.",
    featureKey: FEATURE_KEYS.TASKS,
  },
  {
    target: "[data-tour='member-veranstaltungen']",
    title: "Veranstaltungen",
    content:
      "Sehen Sie kommende Veranstaltungen und melden Sie sich direkt an.",
    featureKey: FEATURE_KEYS.EVENTS,
  },
  {
    target: "[data-tour='member-shop']",
    title: "Shop",
    content:
      "Stöbern Sie im Shop und bestellen Sie Produkte.",
    featureKey: FEATURE_KEYS.SHOP,
  },
  {
    target: "[data-tour='member-survey']",
    title: "Befragungen",
    content:
      "Nehmen Sie an Umfragen teil.",
    featureKey: FEATURE_KEYS.SURVEY,
  },
  {
    target: "[data-tour='member-support']",
    title: "Hilfe & Support",
    content:
      "Bei Fragen oder Problemen erhalten Sie hier Hilfe.",
    featureKey: null,
  },
  {
    target: "[data-tour='member-profil']",
    title: "Mein Profil",
    content:
      "Hier können Sie Ihr Benutzerkonto und Ihre Einstellungen verwalten.",
    featureKey: null,
  },
];

const locale = {
  back: "Zurück",
  close: "Fertig",
  last: "Fertig",
  next: "Weiter",
  nextLabelWithProgress: "Weiter ({step}/{steps})",
  skip: "Überspringen",
};

const MemberGuidedTour = () => {
  const running = useSelector(selectTourRunning);
  const tourType = useSelector(selectTourType);
  const dispatch = useDispatch();
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const stepsRef = useRef([]);

  const { data: tenant } = useGetTenantPlanQuery();
  const planId = normalizePlanId(tenant?.billingPlan || PLAN_IDS.BASIS);

  useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);

  useEffect(() => {
    if (!running || tourType !== "member") {
      setSteps([]);
      setStepIndex(0);
      return;
    }

    const filteredSteps = memberTourSteps
      .filter(
        (step) =>
          !step.featureKey || hasFeatureAccess(planId, step.featureKey),
      )
      .map((step, i) => ({
        ...step,
        disableBeacon: i === 0,
      }));

    if (!filteredSteps.length) {
      dispatch(endTour());
      return;
    }

    const timerId = window.setTimeout(() => {
      const visibleSteps = filteredSteps.filter((step) =>
        document.querySelector(step.target),
      );
      const nextSteps = visibleSteps.length ? visibleSteps : filteredSteps;

      setSteps(nextSteps);
      setStepIndex(0);
    }, 120);

    return () => window.clearTimeout(timerId);
  }, [running, tourType, planId, dispatch]);

  const handleCallback = useCallback(
    (data) => {
      const { status, type, action, index } = data;

      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        dispatch(endTour());
        return;
      }

      if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
        const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        const totalSteps = stepsRef.current.length;

        if (nextIndex >= totalSteps || nextIndex < 0) {
          dispatch(endTour());
          return;
        }

        setStepIndex(nextIndex);
      }
    },
    [dispatch],
  );

  if (!running || tourType !== "member" || !steps.length) return null;

  return (
    <Joyride
      steps={steps}
      run={running}
      stepIndex={stepIndex}
      continuous
      showSkipButton
      showProgress
      scrollToFirstStep
      locale={locale}
      callback={handleCallback}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: "#1976d2",
        },
      }}
    />
  );
};

export default MemberGuidedTour;
